import scenarioService from "../services/scenarios.service";

const state = () => ({
  directoryTree: {},
  isLoading: false,
  // Incremented whenever folder contents change, so the scenario list knows to refresh
  contentsVersion: 0,
  // While the move modal is open, either
  // { type: "scenarios", uuids, name, currentDirectoryUuid } or { type: "directory", uuid, name }
  moveTarget: null,
});

const actions = {
  loadDirectoryTree({ commit }) {
    commit("setIsLoading", true);

    return new Promise((resolve) => {
      scenarioService
        .getDirectoryTree()
        .then((response) => {
          commit("setDirectoryTree", response.directories);
        })
        .finally(() => {
          commit("setIsLoading", false);
          resolve();
        });
    });
  },
  createDirectory({ dispatch }, { name, parentUuid }) {
    return scenarioService
      .createDirectory(name, parentUuid)
      .then((directory) => dispatch("loadDirectoryTree").then(() => directory));
  },
  renameDirectory({ dispatch }, { uuid, name }) {
    return scenarioService
      .renameDirectory(uuid, name)
      .then(() => dispatch("loadDirectoryTree"));
  },
  moveDirectory({ dispatch }, { uuid, parentUuid }) {
    return scenarioService
      .moveDirectory(uuid, parentUuid)
      .then(() => dispatch("loadDirectoryTree"));
  },
  deleteDirectory({ commit, dispatch }, uuid) {
    return scenarioService.deleteDirectory(uuid).then((result) => {
      commit("contentsChanged");
      return dispatch("loadDirectoryTree").then(() => result);
    });
  },
  moveScenarios({ commit }, { uuids, directoryUuid }) {
    return scenarioService
      .moveScenarios(uuids, directoryUuid)
      .then((result) => {
        commit("contentsChanged");
        return result;
      });
  },
};

const mutations = {
  setDirectoryTree(state, newTree) {
    state.directoryTree = newTree;
  },
  setIsLoading(state, isLoading) {
    state.isLoading = isLoading;
  },
  contentsChanged(state) {
    state.contentsVersion++;
  },
  setMoveTarget(state, moveTarget) {
    state.moveTarget = moveTarget;
  },
};

function flatten(directories, depth, parentUuid, result) {
  (directories || []).forEach((dir) => {
    result.push({ uuid: dir.uuid, name: dir.name, depth, parentUuid });
    flatten(dir.children, depth + 1, dir.uuid, result);
  });
  return result;
}

const getters = {
  isLoading: (state) => state.isLoading,
  directoryTree: (state) => state.directoryTree,
  contentsVersion: (state) => state.contentsVersion,
  moveTarget: (state) => state.moveTarget,
  // Depth-first list of { uuid, name, depth, parentUuid }; root folders have a null parentUuid
  flatDirectories: (state) =>
    Array.isArray(state.directoryTree)
      ? flatten(state.directoryTree, 0, null, [])
      : [],
  // Whether the tree has been fetched, so a missing folder can be told apart from one not loaded yet
  isTreeLoaded: (state) => Array.isArray(state.directoryTree),
  // Folders from the root down to the given folder, e.g. [Cardiology, Arrhythmias].
  // Empty for the root, or for a folder that isn't in the tree.
  folderPath: (state, getters) => (uuid) => {
    const path = [];
    let current = getters.flatDirectories.find((d) => d.uuid === uuid);
    while (current) {
      path.unshift(current);
      const parentUuid = current.parentUuid;
      current = getters.flatDirectories.find((d) => d.uuid === parentUuid);
    }
    return path;
  },
};

export default {
  namespaced: true,
  state,
  actions,
  mutations,
  getters,
};
